import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";

const clamp = (n) => Math.max(0, Math.min(1, n));
const DEFAULT_BOX = [0.08, 0.08, 0.92, 0.32];

// Lets a teacher drag / resize the crop rectangle over the original answer
// sheet, then returns { image_id, box:[x0,y0,x1,y1] } (normalized 0..1).
export default function CropAdjuster({ open, sheets, initial, onClose, onSave }) {
  const [imageId, setImageId] = useState(null);
  const [box, setBox] = useState(DEFAULT_BOX);
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const firstId = initial?.image_id || sheets?.[0]?.id || null;
    setImageId(firstId);
    setBox(initial?.box?.length === 4 ? initial.box.map(clamp) : DEFAULT_BOX);
    setSaving(false);
  }, [open, initial, sheets]);

  const toN = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    return [clamp((e.clientX - r.left) / r.width), clamp((e.clientY - r.top) / r.height)];
  };

  const startMove = (e) => { e.stopPropagation(); dragRef.current = { kind: "move", start: toN(e), orig: [...box] }; };
  const startHandle = (e, which) => { e.stopPropagation(); dragRef.current = { kind: which, orig: [...box] }; };

  const onMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const [nx, ny] = toN(e);
    if (d.kind === "move") {
      const [sx, sy] = d.start; const o = d.orig;
      let dx = nx - sx, dy = ny - sy;
      dx = Math.max(-o[0], Math.min(dx, 1 - o[2]));
      dy = Math.max(-o[1], Math.min(dy, 1 - o[3]));
      setBox([o[0] + dx, o[1] + dy, o[2] + dx, o[3] + dy]);
    } else if (d.kind === "br") {
      setBox((b) => [b[0], b[1], Math.max(b[0] + 0.03, nx), Math.max(b[1] + 0.03, ny)]);
    } else if (d.kind === "tl") {
      setBox((b) => [Math.min(b[2] - 0.03, nx), Math.min(b[3] - 0.03, ny), b[2], b[3]]);
    }
  };
  const end = () => { dragRef.current = null; };

  const url = imageId ? sheets.find((s) => s.id === imageId)?.url : null;
  const pct = (n) => `${n * 100}%`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="crop-adjuster">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Adjust the answer crop</DialogTitle>
          <DialogDescription>Drag the box to move it, drag a corner to resize. This crops the answer from the original sheet.</DialogDescription>
        </DialogHeader>

        {sheets.length > 1 && (
          <div className="flex gap-2 flex-wrap" data-testid="crop-sheet-picker">
            {sheets.map((s, i) => (
              <button key={s.id} onClick={() => setImageId(s.id)} className={`rounded-lg overflow-hidden border-2 ${imageId === s.id ? "border-sky-500" : "border-transparent"}`} data-testid={`crop-sheet-${i}`}>
                <img src={s.url} alt={`sheet ${i + 1}`} className="h-14 w-auto" />
              </button>
            ))}
          </div>
        )}

        <div
          ref={wrapRef}
          className="relative select-none touch-none rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100"
          onPointerMove={onMove}
          onPointerUp={end}
          onPointerLeave={end}
          data-testid="crop-canvas"
        >
          {url && <img src={url} alt="answer sheet" className="block w-full h-auto pointer-events-none" draggable={false} />}
          <div className="absolute inset-0 bg-black/40" style={{ clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 ${pct(box[1])}, ${pct(box[0])} ${pct(box[1])}, ${pct(box[0])} ${pct(box[3])}, ${pct(box[2])} ${pct(box[3])}, ${pct(box[2])} ${pct(box[1])}, 0 ${pct(box[1])})` }} />
          <div
            className="absolute border-2 border-sky-400 cursor-move"
            style={{ left: pct(box[0]), top: pct(box[1]), width: pct(box[2] - box[0]), height: pct(box[3] - box[1]) }}
            onPointerDown={startMove}
            data-testid="crop-box"
          >
            <span onPointerDown={(e) => startHandle(e, "tl")} className="absolute -left-2 -top-2 h-4 w-4 rounded-full bg-sky-500 border-2 border-white cursor-nwse-resize" data-testid="crop-handle-tl" />
            <span onPointerDown={(e) => startHandle(e, "br")} className="absolute -right-2 -bottom-2 h-4 w-4 rounded-full bg-sky-500 border-2 border-white cursor-nwse-resize" data-testid="crop-handle-br" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button
            className="rounded-full bg-indigo-500 hover:bg-indigo-600"
            disabled={saving || !imageId}
            onClick={async () => { setSaving(true); try { await onSave({ image_id: imageId, box: box.map((n) => Math.round(n * 1e4) / 1e4) }); } finally { setSaving(false); } }}
            data-testid="crop-save"
          >
            Save crop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
